import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useLanguage } from '../../contexts/LanguageContext';
import { getServiceById, saveContact } from '../../services/storage';
import type { Service } from '../../types/api.types';
import type { Language } from '../../types/lang.types';

const MotionBox = motion(Box);

const SERVICE_IMAGES: Record<number, string> = {
  1: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1400&q=85',
  2: 'https://images.unsplash.com/photo-1590650153855-d9e808231d41?w=1400&q=85',
  3: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1400&q=85',
  4: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1400&q=85',
  5: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1400&q=85',
  6: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1400&q=85',
};
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1400&q=85';

interface AppointmentForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  preferred_date: string;
  message: string;
}
const EMPTY_FORM: AppointmentForm = { name: '', email: '', phone: '', company: '', preferred_date: '', message: '' };

const STATIC_FEATURES: Record<number, { de: string[]; en: string[]; ar: string[] }> = {
  1: {
    de: [
      'Buchen laufender Geschäftsvorfälle',
      'Kontierung und Erfassung laufender Belege',
      'Buchen von Bank, Kasse, Eingangs- und Ausgangsrechnungen',
      'Monatliche Auswertungen und BWA aus der laufenden Buchhaltung',
      'Kosten- und Leistungsrechnung sowie betriebswirtschaftliche Auswertungen',
    ],
    en: [
      'Posting of ongoing business transactions',
      'Coding and recording of current documents',
      'Posting of bank, cash, incoming and outgoing invoices',
      'Monthly reports and profit & loss statements',
      'Cost accounting and business management analyses',
    ],
    ar: [
      'تسجيل المعاملات التجارية الجارية',
      'ترميز وتسجيل المستندات الجارية',
      'تسجيل البنك والصندوق والفواتير الواردة والصادرة',
      'التقارير الشهرية وقوائم الأرباح والخسائر',
      'محاسبة التكاليف والتحليلات الإدارية',
    ],
  },
  2: {
    de: [
      'Laufende Lohn- und Gehaltsabrechnung',
      'Fertigen der Lohnsteuer-Anmeldungen',
      'Meldungen an Krankenkassen und Sozialversicherungsträger',
      'Baulohnabrechnung inklusive SOKA-BAU-Meldungen',
      'An- und Abmeldungen sowie Pflege der Mitarbeiterstammdaten',
      'Lohnnachweise, Bescheinigungen und Auswertungen',
    ],
    en: [
      'Ongoing payroll and salary processing',
      'Preparation of payroll tax returns',
      'Notifications to health insurers and social security bodies',
      'Construction payroll including SOKA-BAU reports',
      'Registration, deregistration and maintenance of employee master data',
      'Pay slips, certificates and evaluations',
    ],
    ar: [
      'معالجة الرواتب والأجور الجارية',
      'إعداد إقرارات ضريبة الرواتب',
      'الإخطارات لصناديق الصحة وهيئات الضمان الاجتماعي',
      'رواتب قطاع البناء بما في ذلك تقارير SOKA-BAU',
      'تسجيل وإلغاء تسجيل الموظفين وصيانة بياناتهم الأساسية',
      'كشوف الرواتب والشهادات والتقارير',
    ],
  },
  3: {
    de: [
      'Analyse Ihrer Unternehmenszahlen',
      'Auswertung von BWA und Kennzahlen',
      'Liquiditätsübersichten und Kostenanalysen',
      'Unterstützung bei Planung und Organisation',
      'Vorbereitung von Unterlagen für Bank oder Behörden',
      'Optimierung kaufmännischer Abläufe und digitaler Prozesse',
    ],
    en: [
      'Analysis of your business figures',
      'Evaluation of BWA and key metrics',
      'Liquidity overviews and cost analyses',
      'Support with planning and organisation',
      'Preparation of documents for banks or authorities',
      'Optimisation of commercial processes and digital workflows',
    ],
    ar: [
      'تحليل أرقام شركتك',
      'تقييم BWA والمؤشرات الرئيسية',
      'نظرة عامة على السيولة وتحليلات التكاليف',
      'الدعم في التخطيط والتنظيم',
      'إعداد المستندات للبنوك أو الجهات الحكومية',
      'تحسين العمليات التجارية والعمليات الرقمية',
    ],
  },
};

const WHY_US = {
  de: ['Über 5 Jahre Projekterfahrung', 'Persönlicher Ansprechpartner', 'Agile Arbeitsweise', 'Transparente Kommunikation', 'Schnelle Umsetzung'],
  en: ['5+ years of project experience', 'Dedicated contact person', 'Agile methodology', 'Transparent communication', 'Fast delivery'],
  ar: ['أكثر من 5 سنوات خبرة', 'مسؤول شخصي مخصص', 'منهجية رشيقة', 'تواصل شفاف', 'تسليم سريع'],
};

const PROCESS = {
  de: ['Erstes Gespräch & Anforderungsanalyse', 'Konzept & Angebot', 'Entwicklung & regelmäßige Updates', 'Übergabe & Support'],
  en: ['Initial call & requirements analysis', 'Concept & proposal', 'Development & regular updates', 'Handover & support'],
  ar: ['مكالمة أولية وتحليل المتطلبات', 'المفهوم والعرض', 'التطوير والتحديثات المنتظمة', 'التسليم والدعم'],
};

const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentLanguage, t } = useLanguage();
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<AppointmentForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    getServiceById(id ?? '').then((s) => { setService(s); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  const titleKey = `title_${currentLanguage}` as `title_${Language}`;
  const descKey  = `description_${currentLanguage}` as `description_${Language}`;
  const lang     = currentLanguage as 'de' | 'en' | 'ar';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      saveContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        message: `[Terminanfrage – ${service?.[titleKey]}]\nWunschdatum: ${form.preferred_date}\n\n${form.message}`,
      });
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch {
      setSubmitError(t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
        <CircularProgress size={36} thickness={2} sx={{ color: '#111' }} />
      </Box>
    );
  }

  if (!service) {
    return (
      <Container maxWidth="lg" sx={{ py: 16, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 3 }}>Service nicht gefunden</Typography>
        <Button component={RouterLink} to="/services" startIcon={<ArrowBackIcon />}>Zurück</Button>
      </Container>
    );
  }

  const imgSrc = SERVICE_IMAGES[service.order_index] ?? FALLBACK_IMAGE;

  return (
    <Box>
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f9f9f9 0%, #ffffff 60%)',
          borderBottom: '1px solid #e8e8e8',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Button
            component={RouterLink}
            to="/services"
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{ color: 'text.secondary', mb: 5, px: 0, fontWeight: 400, '&:hover': { bgcolor: 'transparent', color: '#111', textDecoration: 'none' } }}
          >
            {t('services.title')}
          </Button>

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <MotionBox initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}
                >
                  TOP BHS
                </Typography>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.8rem', sm: '3.5rem', md: '4.5rem' },
                    fontWeight: 800,
                    letterSpacing: '-2px',
                    lineHeight: 1.05,
                    mb: 4,
                    color: '#3d3d3d',
                  }}
                >
                  {service[titleKey]}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '1.15rem', lineHeight: 1.85, color: '#555', maxWidth: 520, mb: 5 }}
                >
                  {service[descKey]}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    href="#appointment"
                    endIcon={<CalendarMonthIcon />}
                    sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
                  >
                    {lang === 'de' ? 'Termin vereinbaren' : lang === 'en' ? 'Book Appointment' : 'حجز موعد'}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="outlined"
                    size="large"
                    sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
                  >
                    {t('contact.title')}
                  </Button>
                </Box>
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={5}>
              <MotionBox
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              >
                <Box
                  component="img"
                  src={imgSrc}
                  alt={service[titleKey]}
                  sx={{
                    width: '100%',
                    height: { xs: 260, md: 400 },
                    objectFit: 'cover',
                    borderRadius: 3,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.14)',
                  }}
                />
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── FEATURES / WHAT WE OFFER ─────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#ffffff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            sx={{ mb: 8 }}
          >
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
              {lang === 'de' ? 'Leistungsumfang' : lang === 'en' ? "What's included" : 'ما يشمله'}
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' } }}>
              {lang === 'de' ? 'Was wir bieten' : lang === 'en' ? 'What we offer' : 'ما نقدمه'}
            </Typography>
          </MotionBox>

          {(() => {
            const raw = service.features;
            const apiFeatures: string[] = Array.isArray(raw)
              ? raw as string[]
              : (raw as { de: string[]; en: string[]; ar: string[] })?.[lang] ?? [];
            const staticFeatures = STATIC_FEATURES[service.order_index]?.[lang] ?? [];
            const displayFeatures = apiFeatures.length > 0 ? apiFeatures : staticFeatures;
            return displayFeatures.length > 0 ? (
            <Grid container spacing={3}>
              {displayFeatures.map((feature: string, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={feature}>
                  <MotionBox
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: i * 0.07 }}
                    viewport={{ once: true }}
                    sx={{
                      p: 3.5,
                      border: '1px solid #e8e8e8',
                      borderRadius: 2,
                      height: '100%',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      '&:hover': { borderColor: '#111', boxShadow: '0 4px 20px rgba(0,0,0,0.07)' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: '#3d3d3d',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {feature}
                    </Typography>
                  </MotionBox>
                </Grid>
              ))}
            </Grid>
            ) : null;
          })()}
        </Container>
      </Box>

      {/* ── PROCESS ──────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#f8f8f8', py: { xs: 10, md: 14 }, borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8' }}>
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            sx={{ mb: 8 }}
          >
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
              {lang === 'de' ? 'Wie es läuft' : lang === 'en' ? 'How it works' : 'كيف يعمل'}
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' } }}>
              {lang === 'de' ? 'Unser Prozess' : lang === 'en' ? 'Our Process' : 'عمليتنا'}
            </Typography>
          </MotionBox>

          <Grid container spacing={4}>
            {PROCESS[lang].map((step, i) => (
              <Grid item xs={12} sm={6} md={3} key={step}>
                <MotionBox
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Typography
                    sx={{
                      fontSize: '3.5rem',
                      fontWeight: 800,
                      color: '#e8e8e8',
                      lineHeight: 1,
                      mb: 1.5,
                      letterSpacing: '-2px',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1rem' }}>
                    {step}
                  </Typography>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── WHY US + APPOINTMENT FORM ─────────────────────────────────────── */}
      <Box id="appointment" sx={{ bgcolor: '#ffffff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            {/* Why Us */}
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
                  TOP BHS
                </Typography>
                <Typography variant="h2" sx={{ mb: 5, fontSize: { xs: '2rem', md: '2.75rem' } }}>
                  {lang === 'de' ? 'Warum TOP BHS?' : lang === 'en' ? 'Why TOP BHS?' : 'لماذا TOP BHS؟'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {WHY_US[lang].map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#111', fontSize: 22, flexShrink: 0 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#222' }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 5 }} />

                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {lang === 'de'
                    ? 'Wir stehen für Qualität, Verlässlichkeit und partnerschaftliche Zusammenarbeit. Kontaktiere uns für ein kostenloses Erstgespräch.'
                    : lang === 'en'
                    ? 'We stand for quality, reliability and collaborative partnership. Reach out for a free initial consultation.'
                    : 'نحن نؤمن بالجودة والموثوقية والشراكة. تواصل معنا للحصول على استشارة أولية مجانية.'}
                </Typography>
              </MotionBox>
            </Grid>

            {/* Appointment Form */}
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                sx={{
                  bgcolor: '#3d3d3d',
                  borderRadius: 3,
                  p: { xs: 4, md: 5 },
                  color: 'white',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <CalendarMonthIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.7)' }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                    {lang === 'de' ? 'Termin vereinbaren' : lang === 'en' ? 'Book Appointment' : 'حجز موعد'}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
                  {lang === 'de'
                    ? `Kostenlose Erstberatung für: ${service[titleKey]}`
                    : lang === 'en'
                    ? `Free consultation for: ${service[titleKey]}`
                    : `استشارة مجانية لـ: ${service[titleKey]}`}
                </Typography>

                {submitted ? (
                  <Alert
                    severity="success"
                    sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', color: 'white', '& .MuiAlert-icon': { color: '#6ee7b7' } }}
                  >
                    {t('contact.success')}
                  </Alert>
                ) : (
                  <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                      { name: 'name', label: t('contact.name'), type: 'text', required: true },
                      { name: 'email', label: t('contact.email'), type: 'email', required: true },
                      { name: 'phone', label: t('contact.phone'), type: 'tel', required: false },
                      { name: 'company', label: t('contact.company'), type: 'text', required: false },
                    ].map((field) => (
                      <TextField
                        key={field.name}
                        label={field.label}
                        name={field.name}
                        type={field.type}
                        value={form[field.name as keyof AppointmentForm]}
                        onChange={handleChange}
                        required={field.required}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                            '&.Mui-focused fieldset': { borderColor: 'white' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                          '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                        }}
                      />
                    ))}

                    <TextField
                      label={lang === 'de' ? 'Wunschdatum' : lang === 'en' ? 'Preferred Date' : 'التاريخ المفضل'}
                      name="preferred_date"
                      type="date"
                      value={form.preferred_date}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          colorScheme: 'dark',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                          '&.Mui-focused fieldset': { borderColor: 'white' },
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                      }}
                    />

                    <TextField
                      label={t('contact.message')}
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                          '&.Mui-focused fieldset': { borderColor: 'white' },
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                      }}
                    />

                    {submitError && (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>{submitError}</Alert>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={submitting}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        mt: 1,
                        bgcolor: 'white',
                        color: '#3d3d3d',
                        py: 1.5,
                        fontSize: '0.95rem',
                        '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-1px)' },
                        '&:disabled': { bgcolor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.5)' },
                      }}
                    >
                      {submitting
                        ? t('common.loading')
                        : lang === 'de' ? 'Anfrage senden' : lang === 'en' ? 'Send Request' : 'إرسال الطلب'}
                    </Button>
                  </Box>
                )}
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ServiceDetailPage;
